import React, { useState, useEffect } from 'react';
import { X, Cookie } from 'lucide-react';
import { initializeGTM, updateConsentMode, initializeConsentMode } from '../lib/gtm';
import { useTranslation } from '../lib/LanguageContext';

// TypeScript types for cookie consent
export interface ConsentState {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

interface CookieConsentProps {
  onConsentChange?: (consent: ConsentState) => void;
}

const STORAGE_KEY = 'simplestickers_cookie_consent';

// Default consent state
const defaultConsentState: ConsentState = {
  necessary: true,   // Always true - required for basic functionality
  analytics: false,  // User choice
  marketing: false,  // User choice
  timestamp: Date.now()
};

export function CookieConsent({ onConsentChange }: CookieConsentProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [consent, setConsent] = useState<ConsentState>(defaultConsentState);
  const { t } = useTranslation();

  // Check if consent has already been given
  useEffect(() => {
    // Initialize consent mode with default (denied) state
    initializeConsentMode();
    
    const savedConsent = localStorage.getItem(STORAGE_KEY);
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent) as ConsentState;
        setConsent(parsed);
        onConsentChange?.(parsed);
        
        // Update GTM consent mode with saved consent
        updateConsentMode(parsed);
        
        // Initialize GTM if analytics consent was given
        if (parsed.analytics) {
          initializeGTM();
        }
      } catch (error) {
        console.error('Error parsing stored consent:', error);
        setIsVisible(true);
      }
    } else {
      setIsVisible(true);
    }
  }, [onConsentChange]);

  // Save consent to localStorage
  const saveConsent = (newConsent: ConsentState) => {
    const consentWithTimestamp = {
      ...newConsent,
      timestamp: Date.now()
    };
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consentWithTimestamp));
    setConsent(consentWithTimestamp);
    onConsentChange?.(consentWithTimestamp);
    
    // Update GTM consent mode
    updateConsentMode(consentWithTimestamp);
    
    // Initialize GTM if analytics consent was given
    if (consentWithTimestamp.analytics) {
      initializeGTM();
    }
    
    setIsVisible(false);
  };

  // Handle accepting all cookies
  const handleAccept = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now()
    });
  };

  // Handle declining non-essential cookies
  const handleDecline = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: Date.now()
    });
  };

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-4 shadow-lg z-50 border-t-2 border-indigo-500">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <Cookie className="h-5 w-5 text-indigo-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-gray-300 leading-relaxed">
                {t('cookieConsentMessage')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={handleDecline}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white border border-gray-600 hover:border-gray-500 rounded-md transition-colors duration-200"
            >
              {t('decline')}
            </button>
            <button
              onClick={handleAccept}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors duration-200"
            >
              {t('acceptAll')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Utility functions for external use (GTM integration)
export const getCookieConsent = (): ConsentState | null => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

export const setCookieConsent = (consent: ConsentState): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
};

export const hasConsentFor = (category: keyof ConsentState): boolean => {
  const consent = getCookieConsent();
  return consent ? consent[category] === true : false;
};

export const clearCookieConsent = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};